import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Card } from '../utils/cardUtils'
import {
  createShuffledDeck,
  dealCards,
  canMoveToFoundation,
  canMoveToTableau,
} from '../utils/cardUtils'

export type CardSource = 'tableau' | 'waste' | 'foundation'

export interface SelectedCard {
  source: CardSource
  sourceIndex: number // Column index for tableau, foundation index, 0 for waste
  cardIndex: number   // Index within the pile
}

interface SolitaireState {
  tableau: Card[][]
  foundations: Card[][]
  stock: Card[]
  waste: Card[]
  gameStatus: 'playing' | 'won'
  moves: number
  selectedCard: SelectedCard | null

  // Actions
  newGame: () => void
  drawCard: () => void
  selectCard: (source: CardSource, sourceIndex: number, cardIndex: number) => void
  moveToFoundation: (foundationIndex: number) => void
  moveToTableau: (columnIndex: number) => void
  clearSelection: () => void
  tryAutoMoveToFoundation: (source: CardSource, sourceIndex: number) => boolean
}

export const useSolitaireStore = create<SolitaireState>()(
  persist(
    (set, get) => ({
      tableau: [[], [], [], [], [], [], []],
      foundations: [[], [], [], []],
      stock: [],
      waste: [],
      gameStatus: 'playing',
      moves: 0,
      selectedCard: null,

      newGame: () => {
        const deck = createShuffledDeck()
        const { tableau, stock } = dealCards(deck)
        set({
          tableau,
          foundations: [[], [], [], []],
          stock,
          waste: [],
          gameStatus: 'playing',
          moves: 0,
          selectedCard: null,
        })
      },

      drawCard: () => {
        const state = get()
        if (state.gameStatus !== 'playing') return

        if (state.stock.length === 0) {
          // Recycle waste back to stock (reversed, face down)
          if (state.waste.length === 0) return
          const newStock = state.waste.map(c => ({ ...c, faceUp: false })).reverse()
          set({ stock: newStock, waste: [], selectedCard: null })
        } else {
          // Draw one card from stock to waste
          const card = { ...state.stock[state.stock.length - 1]!, faceUp: true }
          set({
            stock: state.stock.slice(0, -1),
            waste: [...state.waste, card],
            selectedCard: null,
          })
        }
      },

      selectCard: (source, sourceIndex, cardIndex) => {
        const state = get()
        if (state.gameStatus !== 'playing') return

        // If same card is clicked, deselect
        if (
          state.selectedCard &&
          state.selectedCard.source === source &&
          state.selectedCard.sourceIndex === sourceIndex &&
          state.selectedCard.cardIndex === cardIndex
        ) {
          set({ selectedCard: null })
          return
        }

        set({ selectedCard: { source, sourceIndex, cardIndex } })
      },

      clearSelection: () => {
        set({ selectedCard: null })
      },

      moveToFoundation: (foundationIndex) => {
        const state = get()
        if (!state.selectedCard || state.gameStatus !== 'playing') return

        const { source, sourceIndex, cardIndex } = state.selectedCard
        let card: Card | undefined
        let newTableau = state.tableau.map(col => [...col])
        let newWaste = [...state.waste]
        let newFoundations = state.foundations.map(f => [...f])

        // Get the card to move (must be the top card for foundation moves)
        if (source === 'tableau') {
          const column = newTableau[sourceIndex]!
          // Can only move top card to foundation
          if (cardIndex !== column.length - 1) {
            set({ selectedCard: null })
            return
          }
          card = column[cardIndex]
        } else if (source === 'waste') {
          if (cardIndex !== state.waste.length - 1) {
            set({ selectedCard: null })
            return
          }
          card = newWaste[cardIndex]
        } else if (source === 'foundation') {
          // Moving from foundation to foundation (rare but possible)
          const fromFoundation = newFoundations[sourceIndex]!
          if (cardIndex !== fromFoundation.length - 1) {
            set({ selectedCard: null })
            return
          }
          card = fromFoundation[cardIndex]
        }

        if (!card || !canMoveToFoundation(card, newFoundations[foundationIndex]!)) {
          set({ selectedCard: null })
          return
        }

        // Remove card from source
        if (source === 'tableau') {
          newTableau[sourceIndex] = newTableau[sourceIndex]!.slice(0, -1)
          // Flip the new top card if there is one
          const col = newTableau[sourceIndex]!
          if (col.length > 0 && !col[col.length - 1]!.faceUp) {
            col[col.length - 1] = { ...col[col.length - 1]!, faceUp: true }
          }
        } else if (source === 'waste') {
          newWaste = newWaste.slice(0, -1)
        } else if (source === 'foundation') {
          newFoundations[sourceIndex] = newFoundations[sourceIndex]!.slice(0, -1)
        }

        // Add card to foundation
        newFoundations[foundationIndex] = [...newFoundations[foundationIndex]!, card]

        // Check for win
        const isWon = newFoundations.every(f => f.length === 13)

        set({
          tableau: newTableau,
          waste: newWaste,
          foundations: newFoundations,
          moves: state.moves + 1,
          selectedCard: null,
          gameStatus: isWon ? 'won' : 'playing',
        })
      },

      moveToTableau: (columnIndex) => {
        const state = get()
        if (!state.selectedCard || state.gameStatus !== 'playing') return

        const { source, sourceIndex, cardIndex } = state.selectedCard
        let newTableau = state.tableau.map(col => [...col])
        let newWaste = [...state.waste]
        let newFoundations = state.foundations.map(f => [...f])
        let cardsToMove: Card[] = []

        // Get card(s) to move
        if (source === 'tableau') {
          const column = newTableau[sourceIndex]!
          // Can move multiple cards from tableau
          cardsToMove = column.slice(cardIndex)
          if (cardsToMove.length === 0 || !cardsToMove[0]!.faceUp) {
            set({ selectedCard: null })
            return
          }
        } else if (source === 'waste') {
          if (cardIndex !== state.waste.length - 1) {
            set({ selectedCard: null })
            return
          }
          const card = newWaste[cardIndex]
          if (!card) {
            set({ selectedCard: null })
            return
          }
          cardsToMove = [card]
        } else if (source === 'foundation') {
          const fromFoundation = newFoundations[sourceIndex]!
          if (cardIndex !== fromFoundation.length - 1) {
            set({ selectedCard: null })
            return
          }
          const card = fromFoundation[cardIndex]
          if (!card) {
            set({ selectedCard: null })
            return
          }
          cardsToMove = [card]
        }

        // Validate move
        if (!canMoveToTableau(cardsToMove[0]!, newTableau[columnIndex]!)) {
          set({ selectedCard: null })
          return
        }

        // Remove cards from source
        if (source === 'tableau') {
          newTableau[sourceIndex] = newTableau[sourceIndex]!.slice(0, cardIndex)
          // Flip the new top card if there is one
          const col = newTableau[sourceIndex]!
          if (col.length > 0 && !col[col.length - 1]!.faceUp) {
            col[col.length - 1] = { ...col[col.length - 1]!, faceUp: true }
          }
        } else if (source === 'waste') {
          newWaste = newWaste.slice(0, -1)
        } else if (source === 'foundation') {
          newFoundations[sourceIndex] = newFoundations[sourceIndex]!.slice(0, -1)
        }

        // Add cards to target column
        newTableau[columnIndex] = [...newTableau[columnIndex]!, ...cardsToMove]

        set({
          tableau: newTableau,
          waste: newWaste,
          foundations: newFoundations,
          moves: state.moves + 1,
          selectedCard: null,
        })
      },

      tryAutoMoveToFoundation: (source, sourceIndex) => {
        const state = get()
        if (state.gameStatus !== 'playing') return false

        let card: Card | undefined
        if (source === 'tableau') {
          const column = state.tableau[sourceIndex]!
          card = column[column.length - 1]
        } else if (source === 'waste') {
          card = state.waste[state.waste.length - 1]
        }

        if (!card) return false

        // Try to find a foundation to move to
        for (let i = 0; i < 4; i++) {
          if (canMoveToFoundation(card, state.foundations[i]!)) {
            // Manually set selection and move
            const cardIndex = source === 'tableau'
              ? state.tableau[sourceIndex]!.length - 1
              : state.waste.length - 1
            set({ selectedCard: { source, sourceIndex, cardIndex } })
            get().moveToFoundation(i)
            return true
          }
        }
        return false
      },
    }),
    {
      name: 'solitaire-game-state',
      version: 1,
      partialize: (state) => ({
        tableau: state.tableau,
        foundations: state.foundations,
        stock: state.stock,
        waste: state.waste,
        gameStatus: state.gameStatus,
        moves: state.moves,
      }),
    }
  )
)
